import { KnowledgeBase } from '../data/database';
import { vectorStoreService } from '../services/vector/vectorStore';
import { useAgentStore } from '../store/agentStore';

/**
 * Creates a minimal system prompt for the OpenAI API
 * 
 * When using the Assistant API or vector store, we don't need to include
 * all the product and sector information in the system prompt since:
 * 1. The Assistant already has this knowledge from training
 * 2. The vector store provides relevant context dynamically
 * 
 * This approach reduces token usage and improves response quality.
 */
export function createSystemPrompt(database: KnowledgeBase, language: string): string {
  // Get current agent
  const { currentAgent } = useAgentStore.getState();
  
  // Only include core identity and guidelines
  return `${createIdentityPrompt(database.generalInfo, currentAgent.name)}

${createGuidelinesPrompt(language)}`;
}

/**
 * Creates a prompt with just the company identity information
 */
function createIdentityPrompt(generalInfo: KnowledgeBase['generalInfo'], agentName: string): string {
  return `Je bent ${agentName}, een klantenservice medewerker van Eye Catching Media. 

OVER ONS:
${generalInfo.companyDescription}

UNIQUE SELLING POINTS:
${generalInfo.usp.map(usp => `- ${usp}`).join('\n')}

ONZE WAARDEN:
${generalInfo.values.map(value => `- ${value}`).join('\n')}`;
}

/**
 * Creates guidelines for response style and tone
 */
function createGuidelinesPrompt(language: string): string {
  return `Richtlijnen:
1. Geef altijd vriendelijke, professionele antwoorden.
2. Focus op de specifieke vraag van de klant.
3. Verwijs naar relevante producten en features uit de geüploade data.
4. Bied aan om meer specifieke informatie te geven indien relevant.
5. Gebruik een conversationele, natuurlijke toon.
6. Antwoord altijd in ${language === 'nl' ? 'het Nederlands' : 'English'} gebaseerd op de taal van de vraag.
7. **Markdown-regels:**
   - Gebruik altijd markdown voor opmaak: vetgedrukte tekst als **tekst**, lijsten met "- ", etc.
   - Voor links: gebruik ALTIJD [tekst](url), nooit platte URLs.
   - Voor e-mailadressen: gebruik ALTIJD [e-mailadres](mailto:e-mailadres), nooit platte e-mailadressen.
8. **Controleerbare links:** Zorg ervoor dat alle links en e-mailadressen klikbaar zijn in de markdown-output.
9. Vermeld geen speculatieve informatie; baseer antwoorden alleen op de geüploade data en vector store-context.
10. Als een antwoord meerdere alinea's bevat, scheid deze met een lege regel.
11. BELANGRIJK: Gebruik NOOIT JavaScript code in je antwoorden.
12. Gebruik NOOIT bullet points (•) aan het begin van regels.`;
}

/**
 * Retrieves relevant context from the vector store based on the user's query
 * This is used to supplement the system prompt with dynamic information
 */
export async function getRelevantContext(query: string, maxResults: number = 3): Promise<string> {
  try {
    const results = await vectorStoreService.searchSimilarContent(query, maxResults);
    
    if (results.length === 0) {
      return '';
    }
    
    return `Relevante informatie voor deze vraag:

${results
  .map(result => {
    // Ensure context is markdown-compatible
    let content = result.content.trim();
    // Replace plain URLs with markdown links if needed
    content = content.replace(
      /\b(https?:\/\/[^\s]+)\b/g,
      url => `[${url}](${url})`
    );
    // Replace plain emails with markdown links if needed
    content = content.replace(
      /\b([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})\b/g,
      email => `[${email}](mailto:${email})`
    );
    return content;
  })
  .join('\n\n')}`;
  } catch (error) {
    console.error('Error retrieving context from vector store:', error);
    return '';
  }
}